/**
 * The diff an edit tool stores in its metadata — and the ONE reader of the
 * legacy full-content shape (#768).
 *
 * Browser-safe (imports only `diff`): the tools write through `fileDiff`, the
 * diff card reads through `diffTextOf`.
 *
 * ── Why a diff and not the two files ────────────────────────────────────
 *
 * `filediff` used to carry `before` and `after` — the WHOLE file, both sides,
 * on every edit. Measured on gnexis (2026-09-23): `tool:edit_file` parts were
 * 308 MB across 1,194 rows; the largest was a one-line edit on a 377 KB file,
 * stored as a 755 KB part. OpenCode stores the same pair, and can afford to:
 * one user, one laptop. Papan stores session DBs per workspace on a shared,
 * multi-tenant box, and the file version store — not the conversation — is the
 * restoration source. So the part keeps what the diff CARD needs and nothing
 * else: `file`, a trimmed unified `diff`, and the `additions`/`deletions`
 * counts. The `filediff` key and its field names stay OpenCode-shaped; its
 * renderer already treats `before`/`after` as optional.
 */
import { createTwoFilesPatch } from 'diff'

/** Context lines kept around each change — what the card has always shown. */
const CONTEXT_LINES = 3

/**
 * Strip the indentation every content line shares — OpenCode's `trimDiff`.
 *
 * A change nested eight levels deep otherwise spends most of the card's width
 * (and the part's bytes) on leading whitespace. Only the common prefix goes, so
 * relative indentation — the part a reader needs — is untouched. Header lines
 * (`Index:`/`===`/`---`/`+++`/`@@`) are left alone.
 *
 * Content is "a `+`/`-`/space line AFTER the first `@@`". OpenCode's version
 * excludes `---`/`+++` by prefix instead, which also skips an added line whose
 * text begins `++` — it would keep its indent while its neighbours lost theirs.
 */
export function trimDiff(diff: string): string {
  const lines = diff.split('\n')
  const content = new Array<boolean>(lines.length).fill(false)
  let inHunk = false
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (line.startsWith('@@')) { inHunk = true; continue }
    content[i] = inHunk && (line.startsWith('+') || line.startsWith('-') || line.startsWith(' '))
  }
  let min = Infinity
  for (let i = 0; i < lines.length; i++) {
    if (!content[i]) continue
    const text = lines[i].slice(1)
    if (text.trim().length === 0) continue
    const indent = text.match(/^(\s*)/)?.[1].length ?? 0
    if (indent < min) min = indent
  }
  if (min === Infinity || min === 0) return diff
  return lines
    .map((line, i) => (content[i] ? line[0] + line.slice(1).slice(min) : line))
    .join('\n')
}

/**
 * The unified diff stored for one file edit: `createTwoFilesPatch` with
 * `CONTEXT_LINES` of context, then `trimDiff`. Callers pass line-ending-
 * normalised text. `''` when nothing changed.
 *
 * For a NEW file the diff is the whole file once (every line an addition) —
 * that is the file, and it is half of what the old shape stored.
 */
export function fileDiff(file: string, before: string, after: string): string {
  if (before === after) return ''
  return trimDiff(createTwoFilesPatch(file, file, before, after, '', '', { context: CONTEXT_LINES }))
}

/** One entry in `metadata.filediff` / `metadata.files[]`, either shape. */
export interface FileDiffEntry {
  file: string
  additions: number
  deletions: number
  /** The stored diff (every part written since #768). */
  diff?: string
  /** write_file only: the file did not exist before. */
  isNew?: boolean
  /** LEGACY (pre-#768 rows): full content, both sides. Read only below. */
  before?: string
  after?: string
}

/**
 * The diff text to render for an entry, whichever shape it was stored in.
 *
 * THE compatibility path: rows written before #768 carry `before`/`after`
 * instead of `diff`; they are diffed here, on read, and nowhere else reads
 * those two fields (`filediff-compat-guard.test.ts` holds that line). Existing
 * rows are not migrated — they shrink as sessions are deleted.
 */
export function diffTextOf(entry: FileDiffEntry): string {
  if (typeof entry.diff === 'string') return entry.diff
  if (typeof entry.before === 'string' || typeof entry.after === 'string') {
    return fileDiff(entry.file, entry.before ?? '', entry.after ?? '')
  }
  return ''
}
